/**
 * Report-Datum passend zur Genauigkeit formatieren.
 *
 * Viele Berichte kennen nur Jahr, Quartal oder Monat. Das Backend speichert
 * dann den ersten Tag der Periode (z. B. 2026-01-01) und setzt
 * DisplayReport.metadata_json.published_precision auf
 * "day" | "month" | "quarter" | "year" | "unknown" (fehlt = "day").
 * Ohne Beruecksichtigung erschien "01.01.2026" fuer einen reinen Jahresbericht.
 *
 *   year    -> "2026"
 *   quarter -> "Q2 2026" (zh: "2026年第二季度")
 *   month   -> "Oktober 2026" / "October 2026" / "2026年10月"
 *   day     -> bisherige Formatierung der jeweiligen Stelle (formatDay-Callback)
 *   unknown -> "" (Aufrufer blendet das Datum aus)
 */

export const REPORT_DATE_PRECISIONS = ["day", "month", "quarter", "year", "unknown"];

const ZH_QUARTERS = ["一", "二", "三", "四"];

const localeFor = (lang) => {
  const lower = String(lang || "").toLowerCase();
  if (lower.startsWith("zh")) return "zh-CN";
  if (lower.startsWith("en")) return "en-US";
  return "de-DE";
};

export function normalizePrecision(value) {
  const p = String(value ?? "").trim().toLowerCase();
  return REPORT_DATE_PRECISIONS.includes(p) ? p : "day";
}

/**
 * Genauigkeit aus einem Report-Objekt lesen. Akzeptiert die API-Form
 * (metadata_json.published_precision) und flache Varianten (z. B. Assistent-Karten).
 */
export function getPublishedPrecision(report) {
  if (!report || typeof report !== "object") return "day";
  return normalizePrecision(
    report.published_precision ??
      report.date_precision ??
      report.metadata_json?.published_precision ??
      report.metadata?.published_precision
  );
}

const parseDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const defaultDayFormatter = (date, lang) =>
  new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);

/**
 * @param {string|Date} value      gespeichertes Datum (ISO)
 * @param {string} precision       day | month | quarter | year | unknown
 * @param {string} lang            aktive Sprache (en/de/zh, i18n.language)
 * @param {(date: Date) => string} [formatDay] Formatierung fuer "day" (Standard: TT.MM.JJJJ je Locale)
 * @returns {string} leer, wenn kein Datum angezeigt werden soll
 */
export function formatReportDate(value, precision, lang, formatDay) {
  const p = normalizePrecision(precision);
  if (p === "unknown") return "";
  const date = parseDate(value);
  if (!date) return "";

  // Perioden-Daten sind "erster Tag der Periode, 00:00 UTC". In UTC auswerten,
  // sonst rutscht z. B. 2026-01-01T00:00Z westlich von UTC ins Jahr 2025.
  const year = date.getUTCFullYear();
  const isZh = String(lang || "").toLowerCase().startsWith("zh");

  if (p === "year") {
    return String(year);
  }
  if (p === "quarter") {
    const q = Math.floor(date.getUTCMonth() / 3) + 1;
    return isZh ? `${year}年第${ZH_QUARTERS[q - 1]}季度` : `Q${q} ${year}`;
  }
  if (p === "month") {
    return new Intl.DateTimeFormat(localeFor(lang), { month: "long", year: "numeric", timeZone: "UTC" }).format(date);
  }
  return typeof formatDay === "function" ? formatDay(date) : defaultDayFormatter(date, lang);
}

/** Bequemlichkeit: Datum + Genauigkeit direkt aus dem Report-Objekt. */
export function formatReportPublished(report, lang, formatDay) {
  if (!report) return "";
  return formatReportDate(report.published_at ?? report.date, getPublishedPrecision(report), lang, formatDay);
}

export default formatReportDate;
